import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const DonationSection = () => {
  const [selectedAmount, setSelectedAmount] = useState(50);
  const [customAmount, setCustomAmount] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const predefinedAmounts = [25, 50, 100, 250, 500];

  const handleAmountSelect = (amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount("");
  };

  const handleCustomAmount = (value: string) => {
    setCustomAmount(value);
    setSelectedAmount(0);
  };

  const handleDonation = async () => {
    const amount = customAmount ? parseFloat(customAmount) : selectedAmount;
    
    try {
      // Validate amount
      const { donationSchema } = await import('@/lib/validations');
      donationSchema.parse({ amount });
    } catch (error: any) {
      toast({
        title: "Montant invalide",
        description: error?.issues?.[0]?.message || "Veuillez sélectionner ou saisir un montant valide pour faire un don.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    
    try {
      // Create Stripe payment link using Supabase edge function
      const { data, error } = await supabase.functions.invoke('create-payment', {
        body: { 
          amount: Math.round(amount * 100) // Convert to cents
        }
      });

      if (error) throw error;
      
      if (data?.url) {
        // Open Stripe payment page in new tab
        window.open(data.url, '_blank');
      } else {
        throw new Error('Impossible de créer le lien de paiement');
      }
    } catch (error) {
      console.error('Donation error:', error);
      toast({
        title: "Erreur de traitement du don",
        description: "Votre don n'a pas pu être traité. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="donation-section" className="py-20 bg-gradient-primary relative overflow-hidden">
      <div className="absolute inset-0 bg-primary/5"></div>
      <div className="container mx-auto px-4 relative">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            Votre don 
            <span className="text-accent-orange"> sauve des vies</span>
          </h2>
          <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            Chaque don nous rapproche d'un pas supplémentaire vers la découverte de traitements pour les maladies neurologiques. 
            Votre soutien rend notre recherche possible.
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <Card className="shadow-strong border-0 backdrop-blur-sm bg-card/95 dark:bg-card/95">
            <CardHeader>
              <CardTitle className="text-2xl text-center text-foreground">
                Faire un Don
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-3 block">
                  Sélectionnez un montant :
                </label>
                <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                  {predefinedAmounts.map((amount) => (
                    <Button
                      key={amount}
                      variant={selectedAmount === amount ? "default" : "outline"}
                      onClick={() => handleAmountSelect(amount)}
                      className={`py-3 ${
                        selectedAmount === amount 
                          ? "bg-primary hover:bg-primary-dark text-white shadow-medium" 
                          : "hover:bg-primary/10 hover:border-primary"
                      }`}
                    >
                      €{amount}
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground mb-2 block">
                  Ou saisissez un montant personnalisé :
                </label>
                <Input
                  type="number"
                  placeholder="Montant en EUR"
                  value={customAmount}
                  onChange={(e) => handleCustomAmount(e.target.value)}
                  className="text-lg py-3"
                />
              </div>

              <div className="bg-accent p-4 rounded-lg">
                <p className="text-sm text-muted-foreground text-center">
                  Avec <span className="font-semibold text-foreground">
                    €{customAmount || selectedAmount}
                  </span> nous pouvons financer{" "}
                  {customAmount || selectedAmount >= 250 
                    ? "une semaine complète de recherche en laboratoire" 
                    : customAmount || selectedAmount >= 100
                    ? "des analyses avancées d'échantillons neurologiques"
                    : "des matériaux essentiels pour nos expériences"
                  }
                </p>
              </div>

              <Button 
                size="lg" 
                className="w-full bg-secondary hover:bg-secondary-light text-white py-4 text-lg font-semibold shadow-medium transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleDonation}
                disabled={isLoading}
              >
                {isLoading ? "Traitement..." : `Faire un don de €${customAmount || selectedAmount}`}
              </Button>

              <div className="mt-6 p-4 bg-muted/50 rounded-lg text-left space-y-4">
                <h4 className="font-semibold text-foreground text-sm">Découvrez tous les avantages de faire un don à la Fondation NeuroResearch</h4>
                
                <p className="text-xs text-muted-foreground">
                  Que vous soyez une personne physique ou morale, vos dons à la Fondation NeuroResearch peuvent être déduits de votre impôt sur le revenu ou de l'impôt sur les sociétés en Espagne uniquement. Notre organisation est couverte par la loi 49/2002, sur le régime fiscal des entités à but non lucratif et les incitations au mécénat. De plus, le décret-loi royal 6/2023 a amélioré les déductions pour les dons aux ONG, donc collaborer présente maintenant encore plus d'avantages.
                </p>

                <div>
                  <h5 className="font-medium text-foreground text-xs mb-1">Déductions pour les particuliers (Impôt sur le revenu)</h5>
                  <p className="text-xs text-muted-foreground mb-2">Le pourcentage que vous pouvez déduire dépend du montant et de si vous donnez de manière continue :</p>
                  <ul className="text-xs text-muted-foreground list-disc list-inside space-y-1">
                    <li>Jusqu'à 250 € par an → déduction de 80%.</li>
                    <li>Montant dépassant 250 € → déduction de 40%.</li>
                    <li>Si vous donnez pendant 3 ans ou plus et maintenez ou augmentez votre contribution → le pourcentage sur la partie dépassant 250 € passe à 45%.</li>
                  </ul>
                </div>

                <div>
                  <h5 className="font-medium text-foreground text-xs mb-1">Exemples pratiques :</h5>
                  <ul className="text-xs text-muted-foreground list-disc list-inside space-y-1">
                    <li>Si vous donnez 250 €, le fisc vous rembourse 200 €, donc votre coût réel sera de 50 €.</li>
                    <li>Si vous donnez 400 € : Deux premières années → déduction de 260 €.</li>
                    <li>À partir de la troisième année → déduction de 267,5 €.</li>
                  </ul>
                  <p className="text-xs text-muted-foreground mt-2 italic">
                    N'oubliez pas : Si votre communauté autonome a des déductions supplémentaires ou ses propres régimes régionaux, l'avantage fiscal peut être encore plus important.
                  </p>
                </div>

                <div>
                  <h5 className="font-medium text-foreground text-xs mb-1">Déductions pour les entreprises (Impôt sur les sociétés)</h5>
                  <ul className="text-xs text-muted-foreground list-disc list-inside space-y-1">
                    <li>40% de déduction générale.</li>
                    <li>50% si le don est récurrent (trois ans ou plus).</li>
                    <li>Limite : 15% de la base imposable.</li>
                    <li>Si vous dépassez la limite, l'excédent peut être appliqué dans les 10 années suivantes.</li>
                  </ul>
                </div>

                <p className="text-xs text-primary font-medium">
                  Si vous souhaitez nous soutenir, contactez-nous.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mt-16 text-white text-center">
          <div>
            <div className="text-3xl font-bold mb-2">€2.5M</div>
            <div className="text-white/80">Collectés en 2025</div>
          </div>
          <div>
            <div className="text-3xl font-bold mb-2">1,200+</div>
            <div className="text-white/80">Donateurs actifs</div>
          </div>
          <div>
            <div className="text-3xl font-bold mb-2">95%</div>
            <div className="text-white/80">Directement à la recherche</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DonationSection;
